import React, { useMemo } from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

/**
 * Reusable signal selector used for both "targets" (labels) and "features".
 *
 * Recommended signals (from the workspace analysis) float to the top and
 * carry a sparkle so the right choice is the obvious one. Selected chips can
 * be tinted per-signal so they match the chart line + grid header colour.
 */
const SignalPicker = ({
  label,
  placeholder,
  helper,
  icon,
  fieldRows,
  value,
  onChange,
  recommended = [],
  excludeKeys = [],
  colorFn,
  accent,
  quickPicks = true,
}) => {
  const theme = useTheme();
  const recSet = useMemo(() => new Set(recommended), [recommended]);
  const excludeSet = useMemo(() => new Set(excludeKeys), [excludeKeys]);

  const options = useMemo(() => {
    const rows = (fieldRows || []).filter((r) => !excludeSet.has(r.key));
    return [...rows].sort((a, b) => {
      const ar = recSet.has(a.key);
      const br = recSet.has(b.key);
      if (ar !== br) return ar ? -1 : 1;
      return (a.key || '').localeCompare(b.key || '');
    });
  }, [fieldRows, recSet, excludeSet]);

  const byKey = useMemo(() => {
    const m = {};
    (fieldRows || []).forEach((r) => {
      m[r.key] = r;
    });
    return m;
  }, [fieldRows]);

  const selectedRows = useMemo(
    () => value.map((k) => byKey[k]).filter(Boolean),
    [value, byKey],
  );

  const unusedRecommended = useMemo(
    () => recommended.filter((k) => !value.includes(k) && !excludeSet.has(k)).slice(0, 6),
    [recommended, value, excludeSet],
  );

  const chipColor = (key, idx) => (colorFn ? colorFn(key, idx) : accent);

  // User-set display name first ("o" -> "Open"), then marketplace original
  // key, then the raw key. Mirrors studioUtils.displayKey.
  const rowLabel = (r) => r?.display_name || r?.original_key || r?.key || '';

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
        {icon}
        <Typography variant="subtitle2" fontWeight={700}>
          {label}
        </Typography>
      </Stack>
      {helper && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {helper}
        </Typography>
      )}

      <Autocomplete
        multiple
        disableCloseOnSelect
        options={options}
        value={selectedRows}
        getOptionLabel={(o) => rowLabel(o)}
        filterOptions={(opts, state) => {
          const q = (state.inputValue || '').trim().toLowerCase();
          if (!q) return opts;
          // Match on the friendly label AND the raw key so power users can
          // still find a renamed signal by its original key.
          return opts.filter((o) =>
            rowLabel(o).toLowerCase().includes(q)
            || (o.key || '').toLowerCase().includes(q));
        }}
        isOptionEqualToValue={(a, b) => a?.key === b?.key}
        onChange={(_e, vals) => onChange(vals.map((v) => v.key))}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => {
            const c = chipColor(option.key, index);
            return (
              <Chip
                {...getTagProps({ index })}
                key={option.key}
                size="small"
                label={rowLabel(option)}
                icon={
                  <Box
                    sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: c, ml: 1 }}
                  />
                }
                sx={{
                  bgcolor: alpha(c, 0.16),
                  border: `1px solid ${alpha(c, 0.5)}`,
                  color: 'text.primary',
                  fontWeight: 600,
                  '& .MuiChip-deleteIcon': { color: alpha(theme.palette.text.primary, 0.5) },
                }}
              />
            );
          })
        }
        renderOption={(props, opt) => {
          const isRec = recSet.has(opt.key);
          return (
            <Box component="li" {...props} key={opt.key}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {rowLabel(opt)}
                  {opt.display_name && (
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                      ({opt.key})
                    </Typography>
                  )}
                </Typography>
                {opt.marketplace && (
                  <Tooltip title={opt.marketplace.listing_title || 'Marketplace signal'}>
                    <StorefrontIcon fontSize="inherit" color="action" />
                  </Tooltip>
                )}
                <Chip
                  label={opt.type || 'unknown'}
                  size="small"
                  variant="outlined"
                  sx={{ height: 18, fontSize: '0.6rem' }}
                />
                {isRec && (
                  <Tooltip title="Recommended by workspace analysis">
                    <AutoAwesomeIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                  </Tooltip>
                )}
              </Stack>
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField {...params} placeholder={placeholder} size="small" />
        )}
      />

      {quickPicks && unusedRecommended.length > 0 && (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
          <AutoAwesomeIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary">
            Suggested:
          </Typography>
          {unusedRecommended.map((k) => (
            <Chip
              key={k}
              size="small"
              variant="outlined"
              label={rowLabel(byKey[k]) || k}
              onClick={() => onChange([...value, k])}
              sx={{ height: 20, fontSize: '0.66rem', cursor: 'pointer' }}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default SignalPicker;
